import { useState, useEffect, useRef } from 'react';
import { X, UserCheck, Search, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { useListParticipants, useActivateParticipant } from '@/hooks/useAuth';
import type { Participant, ParticipantEstadoRegisto } from 'shared';

interface ActivateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Step type ────────────────────────────────────────────────────────────────
type Step = 'search' | 'confirm';

// ─── Estado badge helper ──────────────────────────────────────────────────────
function EstadoBadge({ estado }: { estado: ParticipantEstadoRegisto }) {
  const cfg: Record<ParticipantEstadoRegisto, { label: string; className: string }> = {
    Ativo:    { label: 'Ativo',    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    Pendente: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    Inativo:  { label: 'Inativo',  className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
    Suspenso: { label: 'Suspenso', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  };
  const { label, className } = cfg[estado] ?? cfg.Inativo;
  return (
    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${className}`}>
      {label}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fullName(p: Participant) {
  return `${p.nome} ${p.apelido}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

const MAX_REASON = 500;

export default function ActivateUserModal({ isOpen, onClose }: ActivateUserModalProps) {
  const [step, setStep] = useState<Step>('search');
  const [search, setSearch] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const activateParticipant = useActivateParticipant();
  const { data: participantsData, isLoading: participantsLoading } = useListParticipants();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('search');
      setSearch('');
      setSelectedParticipant(null);
      setReason('');
      setToast(null);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Focus reason textarea when participant is selected
  useEffect(() => {
    if (selectedParticipant && step === 'search') {
      setTimeout(() => reasonRef.current?.focus(), 50);
    }
  }, [selectedParticipant, step]);

  // Auto-dismiss toast after 4s
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  if (!isOpen) return null;

  // ── Derived state — participants this modal can activate ─────────────────────
  //
  // Suspenso: previously active, access revoked by staff.
  // Pendente: every self-registered participant, who is never issued tokens
  //   until a coordinator approves them. For minors, parental authorisation is
  //   collected offline, so approving here is what records who approved and on
  //   what grounds — the Estado dropdown in Utilizadores flips the same column
  //   but writes no audit entry, which is why new accounts belong in this flow.
  const ACTIVATABLE_ESTADOS: ParticipantEstadoRegisto[] = ['Suspenso', 'Pendente'];

  const activatableParticipants = (participantsData?.participants ?? []).filter((p) =>
    ACTIVATABLE_ESTADOS.includes(p.estadoRegisto)
  );

  const filteredParticipants = activatableParticipants.filter((p) => {
    const q = search.toLowerCase();
    return (
      q.length >= 2 &&
      (
        p.nome.toLowerCase().includes(q) ||
        p.apelido.toLowerCase().includes(q) ||
        (p.email ?? '').toLowerCase().includes(q)
      )
    );
  });

  const reasonTrimmed = reason.trim();
  const canProceed = !!selectedParticipant && reasonTrimmed.length > 0 && reasonTrimmed.length <= MAX_REASON;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSelectParticipant = (p: Participant) => {
    setSelectedParticipant(p);
    setSearch(fullName(p));
  };

  const handleProceedToConfirm = () => {
    if (!canProceed) return;
    setStep('confirm');
  };

  const handleConfirmActivate = () => {
    if (!selectedParticipant) return;

    activateParticipant.mutate(
      { participantId: selectedParticipant.id, data: { reason: reasonTrimmed } },
      {
        onSuccess: () => {
          setToast({
            type: 'success',
            message: `Participante "${fullName(selectedParticipant)}" ativado com sucesso.`,
          });
          setStep('search');
          setSearch('');
          setSelectedParticipant(null);
          setReason('');
        },
        onError: (err: Error) => {
          setStep('search');
          setToast({
            type: 'error',
            message: err.message || 'Erro ao ativar participante.',
          });
        },
      }
    );
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="activate-modal-title"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-500" />
            <h2 id="activate-modal-title" className="text-lg font-semibold text-foreground">
              Ativar Participante
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="px-6 py-5 space-y-5">
          {/* Toast */}
          {toast && (
            <div
              className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
                toast.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                  : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
              }`}
            >
              {toast.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0" />
              )}
              {toast.message}
            </div>
          )}

          {step === 'search' && (
            <>
              {/* Info banner — which participants this modal can act on */}
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-800">
                <Info className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <p className="text-xs text-green-800 dark:text-green-400">
                  Apenas participantes com estado <strong>Suspenso</strong> ou <strong>Pendente</strong>{' '}
                  são apresentados na pesquisa.
                  {activatableParticipants.length > 0
                    ? ` ${activatableParticipants.length} participante(s) encontrado(s).`
                    : ' Não existem participantes suspensos ou pendentes de momento.'}
                </p>
              </div>

              {/* Search field */}
              <div>
                <label className="label mb-1.5 block" htmlFor="activate-participant-search">
                  Pesquisar participante suspenso ou pendente
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    id="activate-participant-search"
                    type="text"
                    className="input pl-9"
                    placeholder="Nome, apelido ou email (mín. 2 caracteres)"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setSelectedParticipant(null);
                    }}
                    autoComplete="off"
                    disabled={activatableParticipants.length === 0}
                  />
                </div>

                {/* Search results dropdown */}
                {search.length >= 2 && !selectedParticipant && (
                  <div className="mt-1 border border-border rounded-md bg-card shadow-md max-h-48 overflow-y-auto">
                    {participantsLoading ? (
                      <div className="px-4 py-3 text-sm text-muted-foreground">A carregar...</div>
                    ) : filteredParticipants.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-muted-foreground">Sem resultados.</div>
                    ) : (
                      filteredParticipants.map((p) => (
                        <button
                          key={p.id}
                          className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-accent text-left transition-colors"
                          onClick={() => handleSelectParticipant(p)}
                        >
                          <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                            {p.nome.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">{fullName(p)}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {p.email ?? p.telemovel ?? `ID: ${p.id}`}
                            </div>
                          </div>
                          <EstadoBadge estado={p.estadoRegisto} />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Justification — only shown when participant is selected */}
              {selectedParticipant && (
                <div>
                  {/* Selected participant card */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border mb-4">
                    <div className="w-9 h-9 rounded-full bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 flex items-center justify-center text-sm font-bold shrink-0">
                      {selectedParticipant.nome.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">{fullName(selectedParticipant)}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {selectedParticipant.email ?? selectedParticipant.telemovel ?? `ID: ${selectedParticipant.id}`}
                      </div>
                    </div>
                    <EstadoBadge estado={selectedParticipant.estadoRegisto} />
                  </div>

                  <label className="label mb-1.5 block" htmlFor="activate-reason">
                    Motivo da ativação <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    ref={reasonRef}
                    id="activate-reason"
                    className="input min-h-[100px] resize-y"
                    placeholder="Descreva o motivo da ativação, p. ex. autorização do encarregado de educação recebida (obrigatório)..."
                    value={reason}
                    maxLength={MAX_REASON}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground">
                      Este motivo ficará registado no histórico de auditoria.
                    </p>
                    <span
                      className={`text-xs tabular-nums ${
                        reason.length > MAX_REASON * 0.9
                          ? 'text-red-500 font-medium'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {reason.length}/{MAX_REASON}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {step === 'confirm' && selectedParticipant && (
            /* ── Confirmation step ── */
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200 dark:bg-green-900/10 dark:border-green-800">
                <UserCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800 dark:text-green-400">
                    Tem a certeza que quer ativar{' '}
                    <span className="font-bold">"{fullName(selectedParticipant)}"</span>?
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-500 mt-1">
                    Esta acção irá dar ao participante acesso ao sistema com o estado <strong>Ativo</strong>.
                  </p>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Motivo registado
                </p>
                <p className="text-sm text-foreground break-words">{reasonTrimmed}</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          {step === 'search' ? (
            <>
              <button onClick={onClose} className="btn-outline text-sm">
                Cancelar
              </button>
              <button
                onClick={handleProceedToConfirm}
                disabled={!canProceed}
                className="text-sm flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserCheck className="w-4 h-4" />
                Ativar
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep('search')}
                className="btn-outline text-sm"
                disabled={activateParticipant.isPending}
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmActivate}
                disabled={activateParticipant.isPending}
                className="text-sm flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserCheck className="w-4 h-4" />
                {activateParticipant.isPending ? 'A ativar...' : 'Confirmar Activação'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
